import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  MarketingPermissions,
  OrderManagementPermissions,
} from "@redotech/redo-model/permissions";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import { useCustomAnalyticsReportsFeatureFlag } from "../../../../analytics/reports/custom/hooks/use-custom-analytics-reports-feature-flag";
import { MarketingCloudStatusesContext } from "../../../../redo-suite/marketing-cloud";

export const AnalyticsItems = memo(function AnalyticsItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const { emailSmsEnabled, orderTrackingEnabled } = useRequiredContext(
    MarketingCloudStatusesContext,
  );

  const canViewMarketingAnalytics = useHasPermission(
    MarketingPermissions.VIEW_MARKETING_ANALYTICS,
  );
  const canViewOrderTrackingAnalytics = useHasPermission(
    MarketingPermissions.VIEW_ORDER_TRACKING_ANALYTICS,
  );
  const canViewMarketing = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );
  const canViewReports = useHasPermission(
    OrderManagementPermissions.VIEW_REPORTS,
  );
  const customReportsEnabled =
    useCustomAnalyticsReportsFeatureFlag().data ?? false;

  const showMarketing = emailSmsEnabled && canViewMarketingAnalytics;
  const showOrderTracking =
    orderTrackingEnabled && canViewOrderTrackingAnalytics;
  const showRecover = !!team.settings.recover?.enabled && canViewMarketing;
  const showReports = canViewReports && customReportsEnabled;

  const shouldShowAnalytics =
    showMarketing || showOrderTracking || showRecover || showReports;

  const defaultRoute = showMarketing
    ? routes.marketingAnalyticsHref
    : showOrderTracking
      ? `${routes.marketingBaseUrl}/analytics/order-tracking`
      : showRecover
        ? `${routes.marketingBaseUrl}/analytics/recover`
        : showReports
          ? routes.marketingAnalyticsReportsHref
          : "";

  const analyticsRoute = `${routes.marketingBaseUrl}/analytics`;
  const anyChildSelected = useMatch(`${analyticsRoute}/*`);

  if (!shouldShowAnalytics) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: defaultRoute }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName="Analytics"
      id="analytics"
      UnfilledIcon={BarChart10Icon}
    >
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.marketingAnalyticsHref }}
        friendlyName="Marketing"
        hidden={!showMarketing}
        id="analytics-marketing"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.marketingBaseUrl}/analytics/order-tracking`,
        }}
        friendlyName="Order tracking"
        hidden={!showOrderTracking}
        id="analytics-order-tracking"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.marketingBaseUrl}/analytics/recover`,
        }}
        friendlyName="Recover"
        hidden={!showRecover}
        id="analytics-recover"
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.marketingAnalyticsReportsHref }}
        beta
        friendlyName="Custom reports"
        hidden={!showReports}
        id="analytics-reports"
      />
    </RedoSecondaryNavItem>
  );
});
